import { getFormErrorMessage } from "@core/utility/get-error-message";
import {
  InvoiceFormRule,
  InvoiceFormSchema,
  QuotationFormRule,
  QuotationFormSchema,
} from "@factory/form/form.rule";
import { useFormDefaultQuotation } from "@factory/form/form-default-quotation.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseQuotationModel } from "@factory/model/query-model-quotation";
import {
  useQyDeleteQuotation,
  useQyGetQuotationById,
  useQyUpdateQuotation,
} from "@factory/query/quotation.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { AxiosError } from "axios";
import html2pdf from "html2pdf.js";
import { confirmDialog } from "primereact/confirmdialog";
import { useRef, useState } from "react";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { MessageResponseDto } from "../../../../api";
import { invoiceFormDefault } from "../../../factory/form/form.default";
import { useQyCreateInvoice } from "../../../factory/query/invoice.query";

export function useEditQuotation() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const fileRef = useRef<HTMLDivElement>(null);
  const { getDefault } = useFormDefaultQuotation();
  const navigate = useNavigate();
  const { mapUpdateQuotation, mapDeleteQuotation, mapCreateInvoice } =
    useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { quotationId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Create Invoice", type: "split", value: ActionEnum.Approve },
    { label: "Download", type: "more", value: ActionEnum.Download },
    { label: "Update", type: "more", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];
  const [downloading, setDownloading] = useState(false);

  // QUERY DECLARATION
  // API GETBYID Quotation
  const handleGetApiSuccess = (data: GetResponseQuotationModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

    setValue("basePackage", responseData?.base_package || "");
    setValue("packageName", responseData?.base_package_name || "");
    setValue("client", responseData?.client || "");
    setValue("clientName", responseData?.client_name || "");
    setValue("quotationDate", responseData?.quotation_date || "");
    setValue("location", responseData?.location || "");
    setValue("locationName", responseData?.location_name || "");
    setValue("startDate", formatDate(responseData?.start_date) || "");
    setValue("endDate", formatDate(responseData?.end_date) || "");
    setValue("numAdults", responseData?.num_adults);
    setValue("numChildren", responseData?.num_children);
    setValue("numNights", responseData?.num_nights);
    setValue("numDays", responseData?.num_days);
    setValue("agent", responseData?.agent || "");
    setValue("agentMarkup", responseData?.agent_markup);
    setValue("childRateTotal", responseData?.child_rate_total);
    setValue("adultRateTotal", responseData?.adult_rate_total);
    setValue("status", responseData?.status);
    setValue("amount", responseData?.amount);
    setValue("packageInclusions", responseData?.package_inclusions);
    setValue("customRows", responseData?.custom_rows);
    setValue("hideTotal", Boolean(responseData?.hide_total));
    setValue("customDays", responseData?.custom_days);

    const transportItems = responseData?.transport?.map((item) => {
      const childPrices = item.child_prices;
      const transportItem = {
        code: item.code,
        description: item.description,
        quotation: item.quotation,
        transportType: item.transport_type,
        transportTypeName: item.transport_type_name,
        fromLocation: item.from_location,
        fromLocationName: item.from_location_name,
        toLocation: item.to_location,
        toLocationName: item.to_location_name,
        pricePerPax: item.price_per_pax,
        numPeople: item.num_people,
        unit: item.unit,
        amount: item.amount,
        childPrices: childPrices?.map((childPrice) => ({
          code: childPrice.code,
          quotation: childPrice.quotation,
          quoteTransport: childPrice.quote_transport,
          ageBracket: childPrice.age_bracket,
          childPricePerPax: childPrice.child_price_per_pax,
          numChildren: childPrice.num_children,
          amount: childPrice.amount,
        })),
      };
      return transportItem;
    });
    setValue("transport", transportItems || []);

    const accommodationItems = responseData?.accommodation?.map((item) => {
      const accommodationItem = {
        code: item.code,
        description: item.description,
        quotation: item.quotation,
        hotel: item.hotel,
        hotelName: item.hotel_name,
        roomType: item.room_type,
        roomTypeName: item.room_type_name,
        numPeople: item.num_people,
        unit: item.unit,
        validFrom: item.valid_from,
        validUntil: item.valid_until,
        name: item.name,
        price: item.price,
        night: item.night,
        withBreakfast: item.with_breakfast, // Added for UI purposes
      };
      return accommodationItem;
    });
    setValue("accommodation", accommodationItems || []);

    const activitiesItems = responseData?.activities?.map((item) => {
      const activitiesItem = {
        code: item.code,
        quotation: item.quotation,
        baseActivity: item.base_activity,
        baseActivityName: item.base_activity_name,
        isModified: item.is_modified,
        description: item.description,
        day: item.day,
        isChildActivity: item.is_child_activity,
        childRate: item.child_rate,
        showAsOptional: item.show_as_optional,
        isFree: item.is_free,
        entranceFee: item.entrance_fee,
        numPeople: item.num_people,
        unit: item.unit,
        name: item.name,
        ratePerPax: item.rate_per_pax,
        amount: item.amount,
      };
      return activitiesItem;
    });
    setValue("activities", activitiesItems || []);
  };
  const {
    data: quotationResponse,
    isLoading,
    isError: quotationError,
  } = useQyGetQuotationById(quotationId || "", handleGetApiSuccess);
  const quotationData = quotationResponse?.data?.[0];

  // API CREATE Invoice
  const handleApiSuccess = (response: MessageResponseDto) => {
    const data = response.data as any;

    showSuccess(`New Invoice is created`);
    navigate(`../../invoice/${data.code}`);
  };
  const extractInvoiceUUID = (text: string): string | null => {
    const regex =
      /Invoice already exists: ([\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12})/i;
    const match = text.match(regex);
    if (match && match[1]) {
      return match[1];
    }
    return null;
  };
  const handleApiError = (err: AxiosError) => {
    const invoiceId = extractInvoiceUUID((err?.response?.data as any)?.message)
    if (invoiceId !== null) {
      navigate(`../../invoice/${invoiceId}`);
    }
  };
  const { mutate: addInvoice } = useQyCreateInvoice(
    handleApiSuccess,
    handleApiError,
  );

  // API UPDATE Quotation
  const handleUpdateApiSuccess = () => {
    showSuccess("Quotation updated successfully");
  };
  const { mutate: updateQuotation, isLoading: isEditLoading } =
    useQyUpdateQuotation(handleUpdateApiSuccess);

  // API DELETE Quotation
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Quotation deleted successfully");
  };
  const { mutate: deleteQuotation } = useQyDeleteQuotation(
    handleDeleteApiSuccess,
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<QuotationFormSchema>({
    defaultValues: getDefault(quotationData),
    resolver: zodResolver(QuotationFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: QuotationFormSchema) => {
    if (!quotationData || !quotationData.code) {
      throw new Error("No quotation data");
    }
    const transports = form.transport?.map((item) => ({
      ...item,
      child_prices: item.childPrices,
    }));
    form.transport = transports;

    const formData = mapUpdateQuotation(form, quotationData.code);
    updateQuotation(formData);
  };
  const handleValidateError = (err: FieldErrors<QuotationFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!quotationData || !quotationData.code) {
      throw new Error("No quotation data found");
    }

    const form = mapDeleteQuotation(quotationData.code);
    deleteQuotation(form);
  };

  // LOCAL Invoice Functions
  const invoiceFormMethod = useForm<InvoiceFormSchema>({
    defaultValues: invoiceFormDefault,
    resolver: zodResolver(InvoiceFormRule),
  });
  const { handleSubmit: handleSubmitInvoice, setValue: setInvoiceValue } =
    invoiceFormMethod;

  const handleValidateInvoice = (form: InvoiceFormSchema) => {
    const formData = mapCreateInvoice(form);
    addInvoice(formData);
  };
  const handleValidateInvoiceError = (err: FieldErrors<InvoiceFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  const handleAction = async (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Approve:
        setInvoiceValue("baseQuote", quotationData?.code || "");
        setInvoiceValue("invoiceDate", new Date().toISOString());
        setInvoiceValue("amount", quotationData?.amount || 0);
        handleSubmitInvoice(
          handleValidateInvoice,
          handleValidateInvoiceError,
        )();
        break;

      case ActionEnum.Download:
        setDownloading(true);
        await downloadPdf(`Quotation-${quotationData?.code}.pdf`);
        setDownloading(false);
        break;

      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

  const downloadPdf = async (fileName: string) => {
    if (!fileRef.current) return;
    const textareas = document.querySelectorAll("textarea");
    textareas.forEach((textarea) => {
      const content = textarea.value.replace(/\n/g, "<br>");
      textarea.outerHTML = `<div 
                                class="text-[0.8em] text-orange-400 p-1 font-semibold w-full placeholder-orange-400"
                              >
                                ${content}
                              </div>`;
    });
    const opt = {
      margin: 0.1,
      filename: fileName,
      image: { type: "jpeg", quality: 1 },
      html2canvas: {
        scale: 2, // Increase scale for better quality
        useCORS: true, // Enable CORS
        allowTaint: true, // Allow cross-origin images
        logging: true, // Enable logging for debugging
        imageTimeout: 15000, // Increase timeout
      },
      jsPDF: {
        orientation: "portrait",
        unit: "pt", // use points
        format: "letter",
      },
      pagebreak: { mode: "avoid-all" },
    };

    html2pdf().set(opt).from(fileRef.current).save();
  };

  return {
    quotationId,
    quotationData,
    isLoading,
    quotationError,
    formMethod,
    actions,
    isEditLoading,
    fileRef,
    downloading,
    setDownloading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}
